import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  type AppointmentCommunicationTemplate,
  type AppointmentEventType,
  type Constant,
  EventTypeName,
  type FieldValidator,
  useFields,
  Utils,
} from '@pbt/pbt-ui-components'

import { ConversationTransport } from '~/api/graphql/generated/types'
import {
  getAppointmentCommunicationInitializationType,
  getCommunicationTransportBackupOption,
} from '~/store/reducers/constants'
import { CommunicationTransportBackupOptionName } from '~/types'
import { getConstantsList } from '~/utils'
import { getCommunicationsMessageMaxLength } from '~/utils/communicationsUtils'
import { useEventType } from '~/utils/useEventType'

const MESSAGE_MAX_LENGTH = getCommunicationsMessageMaxLength(
  ConversationTransport.Sms,
  true,
)

interface UseAutomaticCommunicationFieldsProps {
  automaticCommunication?: AppointmentCommunicationTemplate
  shouldHideTextFields: boolean | undefined
}

export const useAutomaticCommunicationFields = ({
  automaticCommunication,
  shouldHideTextFields,
}: UseAutomaticCommunicationFieldsProps) => {
  const { t } = useTranslation(['Common', 'Businesses', 'Validations'])

  const CommunicationTransportBackupOption: Constant[] = useSelector(
    getCommunicationTransportBackupOption,
  )

  const AppointmentSubTypes: AppointmentEventType['subTypes'] = useEventType(
    EventTypeName.Appointment,
    'subTypes',
  )
  const AppointmentStatuses: AppointmentEventType['states'] = useEventType(
    EventTypeName.Appointment,
    'states',
  )

  const PreferredContactMethodId: string = Utils.findConstantIdByName(
    CommunicationTransportBackupOptionName.PREFERRED_CONTACT_METHOD,
    CommunicationTransportBackupOption,
  )

  const InitializationType: Constant[] = useSelector(
    getAppointmentCommunicationInitializationType,
  )

  const SpecificTimeOptionId: string = Utils.findConstantIdByName(
    'At a specific time',
    InitializationType,
  )

  const textTemplateValidator = ({
    state: { textTemplate },
  }: {
    state: { textTemplate: string }
  }) => textTemplate.length <= MESSAGE_MAX_LENGTH

  return useFields(
    [
      {
        name: 'active',
        label: t('Common:ACTIVE_ONE'),
        type: 'toggle',
        initialValue: automaticCommunication?.active ?? true,
      },
      {
        name: 'name',
        label: t('Common:NAME'),
        validators: ['required'],
        initialValue: automaticCommunication?.name || '',
      },
      {
        name: 'initializationTypeId',
        initialValue:
          automaticCommunication?.initializationTypeId || SpecificTimeOptionId,
      },
      {
        name: 'appointmentTypeIds',
        label: t(
          'Businesses:APPOINTMENT_COMMUNICATIONS.SEND_FOR_APPOINTMENT_TYPES',
        ),
        type: 'select',
        initialValue: automaticCommunication
          ? getConstantsList(
              automaticCommunication.appointmentTypeIds || [],
              AppointmentSubTypes,
            )
          : [],
      },
      {
        name: 'communicationMethodId',
        label: t('Common:DEFAULT'),
        initialValue:
          automaticCommunication?.communicationMethodId ||
          PreferredContactMethodId,
      },
      {
        name: 'communicationMethodPrimaryBackupId',
        label: t('Businesses:APPOINTMENT_COMMUNICATIONS.PRIMARY_BACK_UP'),
        initialValue:
          automaticCommunication?.communicationMethodPrimaryBackupId || '',
      },
      {
        name: 'communicationMethodSecondaryBackupId',
        label: t('Businesses:APPOINTMENT_COMMUNICATIONS.SECONDARY_BACK_UP'),
        initialValue:
          automaticCommunication?.communicationMethodSecondaryBackupId || '',
      },
      {
        name: 'communicationMethodTertiaryBackupId',
        label: t('Businesses:APPOINTMENT_COMMUNICATIONS.TERTIARY_BACK_UP'),
        initialValue:
          automaticCommunication?.communicationMethodTertiaryBackupId || '',
      },
      {
        name: 'polarisCommunicationTemplateId',
        label: t(
          'Businesses:APPOINTMENT_COMMUNICATIONS.POLARIS_COMMUNICATION_TEMPLATE_LABEL',
        ),
        initialValue: automaticCommunication?.polarisCommunicationTemplateId,
      },
      {
        name: 'subject',
        label: t('Common:SUBJECT_WILL_NOT_DISPLAY'),
        validators: ['required'],
        initialValue: automaticCommunication?.subject || '',
      },
      {
        name: 'template',
        label: t('Common:MESSAGE'),
        validators: shouldHideTextFields ? [] : ['required'],
        initialValue: automaticCommunication?.template || '',
      },
      {
        name: 'textTemplate',
        label: t('Common:TEXT_MESSAGE'),
        validators: shouldHideTextFields
          ? []
          : ([
              'required',
              {
                validator: textTemplateValidator,
                validatorName: 'textTemplateValidator',
              },
            ] as FieldValidator[]),
        messages: {
          textTemplateValidator: t(
            'Validations:TEXT_MESSAGE_LENGTH_SHOULD_BE_LESS',
            {
              maxLength: MESSAGE_MAX_LENGTH,
            },
          ),
        },
        initialValue: automaticCommunication?.textTemplate || '',
      },
      {
        name: 'appointmentStateIds',
        label: t(
          'Businesses:APPOINTMENT_COMMUNICATIONS.SEND_FOR_APPOINTMENT_WITH_STATUS_OF',
        ),
        initialValue: automaticCommunication
          ? getConstantsList(
              automaticCommunication.appointmentStateIds || [],
              AppointmentStatuses,
            )
          : [],
        type: 'select',
      },
    ],
    false,
  )
}
